import { useMemo, useRef, useState } from 'react';
import { formatUnits, parseUnits } from 'viem';
import { FloatingInput } from '@/components/ui/floating-input';
import { cn } from '@/lib/utils';
import { DecimalsButton } from './DecimalsButton';
import { CustomDecimalsDialog } from './CustomDecimalsDialog';
import {
  NumberValidationState,
  NumberValueType,
  TransactionBuilderParameterInputProps,
  TransactionBuilderParameterState,
} from './types';
import { getNumberValidationState, getParameterStateForNumber } from './utils';
import { getReadableSolidityName } from '@/lib/solidity-formatting';

function toDateTimeLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatDateDisplay(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';

  // "Dec 16, 2025 3:04 PM GMT-5"
  return `${get('month')} ${get('day')}, ${get('year')} ${get('hour')}:${get(
    'minute'
  )} ${get('dayPeriod')} ${get('timeZoneName')}`.trim();
}

export function NumberInputField({
  value,
  name,
  onChange,
  paramAbiFragment,
  hideDecimalsButton,
  unformattedValue,
  cardLayout = false,
}: TransactionBuilderParameterInputProps): React.ReactElement {
  const decoded: NumberValueType = useMemo(
    () =>
      value
        ? (JSON.parse(value) as NumberValueType)
        : { value: '', decimals: 0, formatType: 'number' },
    [value]
  );

  const [localDecimals, setLocalDecimals] = useState<number>(
    decoded.decimals ?? 0
  );
  const [localFormat, setLocalFormat] = useState<'number' | 'datetime'>(
    (decoded.formatType as 'number' | 'datetime') || 'number'
  );
  const [isCustomDecimalsOpen, setIsCustomDecimalsOpen] = useState(false);
  const hiddenDateTimeRef = useRef<HTMLInputElement | null>(null);

  const label = getReadableSolidityName(
    paramAbiFragment.name || name,
    'Parameter'
  );
  const allowNegative = paramAbiFragment.type.startsWith('int');
  const safeUnformattedValue =
    typeof unformattedValue === 'string'
      ? unformattedValue
      : unformattedValue === null || unformattedValue === undefined
        ? ''
        : String(unformattedValue);

  const emitChange = (
    val: string,
    decimals: number,
    formatType: NumberValueType['formatType']
  ) => {
    const newState: TransactionBuilderParameterState =
      getParameterStateForNumber(
        {
          value: val,
          decimals,
          formatType: formatType || 'number',
        },
        allowNegative
      );
    onChange?.(name, newState);
  };

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const prevCurrent = String(decoded.value ?? '');
    let next = event.target.value.replaceAll(',', '');
    const isDeletion = next.length < prevCurrent.length;

    // Case: value starts with ".", normalize to "0."
    if (next.startsWith('.')) next = `0${next}`;

    // Allow "-" while typing negative numbers (if allowed).
    if (allowNegative && next === '-') {
      emitChange(next, localDecimals, localFormat);
      return;
    }

    const isNegative = allowNegative && next.startsWith('-');
    const abs = isNegative ? next.slice(1) : next;

    // Allow empty
    if (abs === '') {
      emitChange(next, localDecimals, localFormat);
      return;
    }

    // Basic numeric shape validation: digits + optional single decimal point
    if (!/^[0-9]*\.?[0-9]*$/.test(abs)) return;
    // For insertions, enforce integer-only when decimals === 0. For deletions, allow users
    // to delete their way back to a valid integer even if a '.' is still present.
    if (!isDeletion && localDecimals === 0 && abs.includes('.')) return;

    // Enforce decimal precision while typing
    const parts = abs.split('.');
    if (parts.length > 2) return;
    const fraction = parts[1] ?? '';
    // Critical: allow deletions even if still invalid (e.g. removing 1 char from 8 decimals -> 7 decimals).
    if (!isDeletion && fraction.length > localDecimals) return;

    emitChange(isNegative ? `-${abs}` : abs, localDecimals, localFormat);
  };

  const handleFormatChange = (
    formatType: 'number' | 'datetime',
    decimals?: number
  ) => {
    if (formatType === 'datetime') {
      setLocalFormat('datetime');
      setLocalDecimals(0);
      // Store epoch-seconds as the underlying value for datetime mode.
      const epochSeconds = (() => {
        const raw = (decoded.value ?? '').trim();
        if (/^[0-9]+$/.test(raw)) return raw;
        const fallback = safeUnformattedValue.trim();
        if (/^[0-9]+$/.test(fallback)) return fallback;
        return '';
      })();

      emitChange(epochSeconds, 0, 'datetime');
      return;
    }

    const nextDecimals = decimals ?? 0;
    setLocalFormat('number');
    setLocalDecimals(nextDecimals);
    // If we’re switching back from timestamp mode, use the raw epoch-seconds value (not the formatted date).
    if (localFormat === 'datetime') {
      const rawEpoch = (decoded.value ?? '').trim();
      if (!rawEpoch) {
        emitChange('', nextDecimals, 'number');
        return;
      }
      const base = BigInt(rawEpoch);
      const nextDisplay =
        nextDecimals === 0 ? rawEpoch : formatUnits(base, nextDecimals);
      emitChange(nextDisplay, nextDecimals, 'number');
      return;
    }

    // Normal decimals switching: use the existing base-unit value when available.
    const baseUnits = (() => {
      const raw = safeUnformattedValue.trim();
      if (raw && raw !== 'NaN') return raw;

      const current = (decoded.value ?? '').trim();
      if (!current || current === '-') return '';
      const isNegative = current.startsWith('-');
      const abs = isNegative ? current.slice(1) : current;
      if (!abs) return '';
      try {
        const parsed = parseUnits(abs, localDecimals);
        return (isNegative ? BigInt(-1) * parsed : parsed).toString();
      } catch {
        return '';
      }
    })();

    if (!baseUnits) {
      emitChange(decoded.value, nextDecimals, 'number');
      return;
    }

    const base = BigInt(baseUnits);
    const nextDisplay =
      nextDecimals === 0 ? base.toString() : formatUnits(base, nextDecimals);
    emitChange(nextDisplay, nextDecimals, 'number');
  };

  const dateTimeLocalValue =
    localFormat === 'datetime'
      ? (() => {
          const raw = (decoded.value ?? '').trim();
          if (!raw) return '';

          // Accept either ISO/date strings or an epoch-seconds int string.
          const date = /^[0-9]+$/.test(raw)
            ? new Date(Number(raw) * 1000)
            : new Date(raw);

          if (Number.isNaN(date.getTime())) return '';
          return toDateTimeLocalValue(date);
        })()
      : '';

  const dateTimeDisplayValue =
    localFormat === 'datetime'
      ? (() => {
          const raw = (decoded.value ?? '').trim();
          if (!raw) return '';
          const date = /^[0-9]+$/.test(raw)
            ? new Date(Number(raw) * 1000)
            : new Date(raw);
          if (Number.isNaN(date.getTime())) return '';
          return formatDateDisplay(date);
        })()
      : '';

  const epochSecondsForDateTime =
    localFormat === 'datetime' && dateTimeLocalValue
      ? (() => {
          const d = new Date(dateTimeLocalValue);
          if (Number.isNaN(d.getTime())) return '';
          return String(Math.round(d.getTime() / 1000));
        })()
      : '';

  const numberValidationState = getNumberValidationState(
    decoded.value,
    localDecimals,
    allowNegative
  );

  const convertedValue =
    localFormat === 'number' &&
    localDecimals > 0 &&
    numberValidationState === NumberValidationState.VALID &&
    decoded.value
      ? (() => {
          try {
            const isNegative = decoded.value.startsWith('-');
            const abs = isNegative ? decoded.value.slice(1) : decoded.value;
            const raw = parseUnits(abs || '0', localDecimals).toString();
            return isNegative ? `-${raw}` : raw;
          } catch {
            return '';
          }
        })()
      : '';

  const helperText = (() => {
    switch (numberValidationState) {
      case NumberValidationState.INVALID_DECIMALS:
        return localDecimals
          ? `Can't have more than ${localDecimals} decimals.`
          : "Can't contain decimals.";
      case NumberValidationState.INVALID_CANT_BE_NEGATIVE:
        return "Can't be negative.";
      case NumberValidationState.INVALID_CANT_BE_JUST_NEGATIVE:
        return '"-" is not a valid number.';
      case NumberValidationState.INVALID_CANT_END_WITH_DECIMAL:
        return "Can't end with decimal.";
      default:
        return '';
    }
  })();

  return (
    <div
      className={cn(
        'flex flex-col gap-2 w-full',
        cardLayout &&
          'gap-1 [&_input]:h-6 [&_input]:px-1 [&_input]:pr-[130px] [&_button[data-slot=button]]:h-6 [&_button[data-slot=button]]:rounded-full [&_button[data-slot=button]]:text-[10px]'
      )}
    >
      {localFormat === 'datetime' ? (
        <>
          {/* Display-only field. Clicking opens the date picker; user cannot manually edit the date text. */}
          <FloatingInput
            value={dateTimeDisplayValue}
            onChange={() => {
              // no-op (readOnly)
            }}
            readOnly
            placeholder=""
            label={label}
            className={cn(
              'flex-1 cursor-pointer',
              'border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-sm font-mono'
            )}
            onMouseDown={(e) => {
              // Prevent focusing / selecting text; this field is purely a picker trigger.
              e.preventDefault();
              e.stopPropagation();
              const el = hiddenDateTimeRef.current;
              if (!el) return;
              // Prefer the native picker when available (Chromium). Fallback to click/focus.
              const withPicker = el as HTMLInputElement & {
                showPicker?: () => void;
              };
              if (typeof withPicker.showPicker === 'function') {
                withPicker.showPicker();
                return;
              }
              el.focus();
              el.click();
            }}
            endAdornment={
              hideDecimalsButton ? undefined : (
                <DecimalsButton
                  decimals={0}
                  formatType="datetime"
                  onChange={handleFormatChange}
                  onOpenCustomDecimals={() => setIsCustomDecimalsOpen(true)}
                />
              )
            }
          />

          {/* Hidden native picker: drives selection + conversions, but is not directly editable/visible. */}
          <input
            ref={hiddenDateTimeRef}
            className="absolute opacity-0 pointer-events-none h-0 w-0"
            tabIndex={-1}
            type="datetime-local"
            value={dateTimeLocalValue}
            onChange={(e) => {
              const nextLocal = e.target.value;
              if (!nextLocal) {
                emitChange('', 0, 'datetime');
                return;
              }
              const d = new Date(nextLocal);
              if (Number.isNaN(d.getTime())) return;
              const epoch = String(Math.round(d.getTime() / 1000));
              emitChange(epoch, 0, 'datetime');
            }}
          />
        </>
      ) : (
        <FloatingInput
          value={decoded.value}
          onChange={handleValueChange}
          placeholder="0"
          label={label}
          className={cn(
            'flex-1',
            'border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-sm font-mono'
          )}
          endAdornment={
            hideDecimalsButton ? undefined : (
              <DecimalsButton
                decimals={localDecimals}
                formatType={localFormat}
                onChange={handleFormatChange}
                onOpenCustomDecimals={() => setIsCustomDecimalsOpen(true)}
              />
            )
          }
        />
      )}

      {epochSecondsForDateTime && localFormat === 'datetime' ? null : null}

      {(convertedValue || helperText || (cardLayout && safeUnformattedValue)) &&
        localFormat !== 'datetime' && (
          <p
            className={cn(
              'text-xs',
              helperText ? 'text-destructive' : 'text-muted-foreground'
            )}
          >
            {helperText ||
              (cardLayout
                ? `Raw value ${convertedValue || safeUnformattedValue}`
                : `Value without conversion is ${convertedValue}`)}
          </p>
        )}
      <CustomDecimalsDialog
        open={isCustomDecimalsOpen}
        onOpenChange={setIsCustomDecimalsOpen}
        initialDecimals={localDecimals}
        onConfirm={(decimals) => handleFormatChange('number', decimals)}
      />
    </div>
  );
}
